import { Text, ScreenContainer, View } from "@src/components";
import { sizes } from "@src/constants/sizes";
import { router, useLocalSearchParams } from "expo-router";
import { NumericKeyboard } from "@src/components/Pin/NumericKeyboard";
import { PinDots } from "@src/components/Pin/PinDots";
import { StyleSheet } from "react-native-unistyles";
import { usePin } from "@src/hooks/usePin";
import { useCrossFadeSpinner } from "@src/hooks/useCrossFadeSpinner";
import { useCallback } from "react";
import Animated from "react-native-reanimated";
import { ActivityIndicator } from "react-native";
import { useUnistyles } from "react-native-unistyles";
import { FocusedSystemBars } from "@src/components/FocusedSystemBars";
import { BackgroundGradient } from "@src/components/BackgroundGradient";
import { BackRoundedButton } from "@src/components/BackRoundedButton";
import { ContactUsButton } from "@src/components/ContactUsButton";

const ConfirmPinScreen = () => {
  const { pinString } = useLocalSearchParams();
  const { confirmPin, onChangeConfirmPin, onDeletePin, error, isConfirmingPin } = usePin();
  const { theme } = useUnistyles();

  const { contentStyle, spinnerStyle } = useCrossFadeSpinner(isConfirmingPin);

  const handleChangeConfirmPin = useCallback(
    (value: number) => {
      onChangeConfirmPin({ value, pinString: String(pinString) });
    },
    [onChangeConfirmPin, pinString],
  );

  return (
    <>
      <FocusedSystemBars style="light" />
      <BackgroundGradient>
        <View style={{ flex: 1 }}>
          <ScreenContainer
            edges={["top", "bottom"]}
            style={{ backgroundColor: "transparent", flex: 1 }}
            contentMaxWidth={sizes.maxWidthSm}
          >
            <Animated.View style={[{ flex: 1 }, contentStyle]}>
              <View style={styles.backButtonContainer}></View>
              <View mt="9xl" alignItems="center">
                <Text textStyle="h3/regular" textAlign="center" style={styles.text}>
                  Confirm Passcode
                </Text>
              </View>
              <View>
                <View mt="4xl" alignItems="center">
                  <PinDots pinLength={confirmPin.length} />
                </View>
                {error && (
                  <View style={styles.errorContainer}>
                    <Text textStyle="sm/regular" textAlign="center" style={styles.error}>
                      {error}
                    </Text>
                  </View>
                )}
              </View>
              <View flex={1} alignItems="center">
                <NumericKeyboard
                  enabled={!(confirmPin.length === 4)}
                  onPress={handleChangeConfirmPin}
                  onDelete={() => onDeletePin({ isConfirmPin: true })}
                />
              </View>
              <View my="4xl" flexDirection="row" justifyContent="center" alignItems="center" gap="sm">
                <Text textStyle="sm/regular" textAlign="center" style={styles.text}>
                  Having a problem?
                </Text>
                <ContactUsButton />
              </View>
            </Animated.View>
          </ScreenContainer>
          <Animated.View
            style={[StyleSheet.absoluteFillObject, styles.spinner, spinnerStyle]}
            pointerEvents={isConfirmingPin ? "auto" : "none"}
          >
            <ActivityIndicator size="large" color={theme.colors.white.default} />
          </Animated.View>
        </View>
      </BackgroundGradient>
    </>
  );
};

const styles = StyleSheet.create((theme) => ({
  spinner: {
    justifyContent: "center",
    alignItems: "center",
    zIndex: 10,
  },
  backButtonContainer: {
    marginLeft: theme.spacing["4xl"],
    marginTop: theme.spacing["3xl"],
  },
  error: {
    color: theme.colors.status.error,
  },
  errorContainer: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: -theme.spacing["8xl"],
    justifyContent: "center",
    alignItems: "center",
  },
  text: {
    color: theme.colors.interactive.buttons.default_foreground,
  },
}));

export default ConfirmPinScreen;
